import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import socket, { onIdentity, setToken, requestState } from './config/socket';

/**
 * Spieler-Anmeldung fuer den LAN-Abend.
 *
 * Diese Seite funktioniert nur, solange der Admin die Lobby geoeffnet hat, und
 * verlangt den vierstelligen Code, den er in TeamSpeak durchsagt. Danach kennt
 * die Seite den Spieler vor dem Bildschirm -- Voraussetzung dafuer, dass beim
 * Map-Veto nur der Kapitaen klicken darf.
 */
const FEHLER = {
  closed: 'Die Lobby ist gerade nicht offen.',
  code: 'Falscher Code.',
  taken: 'Diesen Spieler hat sich schon jemand geschnappt.',
  already: 'Dieser Browser hat bereits einen Spieler. Erst freigeben lassen.',
  invalid: 'Ungueltige Auswahl.',
  error: 'Da ist etwas schiefgelaufen. Nochmal versuchen.',
};

const JoinView = () => {
  const navigate = useNavigate();

  const [identity, setIdentity] = useState(null);
  const [users, setUsers] = useState([]);
  const [lobby, setLobby] = useState({ status: 'closed', claims: [] });
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => onIdentity(setIdentity), []);

  useEffect(() => {
    socket.on('roster:state', setUsers);
    socket.emit('roster:state', null, (list) => list && setUsers(list));

    return () => socket.off('roster:state', setUsers);
  }, []);

  useEffect(() => {
    socket.on('lobby:state', setLobby);
    socket.on('auth:revoked', () => setIdentity(null));

    requestState();

    return () => {
      socket.off('lobby:state', setLobby);
      socket.off('auth:revoked');
    };
  }, []);

  const claimedIds = new Set(lobby.claims.map((c) => c.playerId));

  const claim = (player) => {
    if (busy || claimedIds.has(player.steamID)) return;

    setError('');
    setBusy(true);

    socket.emit(
      'lobby:claim',
      { code: code.trim(), playerId: player.steamID, playerName: player.nickName },
      (result) => {
        setBusy(false);

        if (!result || !result.ok) {
          setError(FEHLER[result && result.reason] || FEHLER.error);
          return;
        }

        setToken(result.token);
        navigate('/');
      }
    );
  };

  /* Schon angemeldet -- nichts mehr zu tun. */
  if (identity && identity.role === 'player') {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 sm:py-24">
        <h1 className="text-2xl pb-4">Du bist {identity.playerName}</h1>
        <p className="text-gray-500 pb-8">
          Alles erledigt. Auf der Startseite siehst du dein Team und das Map-Veto.
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="text-gray-900 bg-gradient-to-r from-teal-200 to-lime-200 hover:bg-gradient-to-l hover:from-teal-200 hover:to-lime-200 focus:ring-4 focus:outline-none focus:ring-lime-200 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
        >
          Zur Uebersicht
        </button>
      </div>
    );
  }

  /* Lobby zu -- gar nichts anbieten. */
  if (lobby.status !== 'open') {
    return (
      <div className="mx-auto max-w-2xl px-6 py-16 sm:py-24">
        <h1 className="text-2xl pb-4">Lobby geschlossen</h1>
        <p className="text-gray-500">
          Der Admin hat die Anmeldung noch nicht geoeffnet. Sobald sie laeuft, gibt es
          hier einen Code einzugeben.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
      <h1 className="text-2xl pb-2">Wer bist du?</h1>
      <p className="text-gray-500 pb-8">
        Code eingeben, dann den eigenen Spieler antippen.
      </p>

      <div className="pb-8">
        <label className="block text-sm text-gray-500 pb-2" htmlFor="lobby-code">
          Code
        </label>
        <input
          id="lobby-code"
          inputMode="numeric"
          maxLength={4}
          autoComplete="off"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          placeholder="1234"
          className="w-32 tracking-[0.4em] text-center text-xl rounded-lg border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-4 focus:ring-lime-200"
        />
      </div>

      {error && (
        <p className="mb-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {users.map((player) => {
          const taken = claimedIds.has(player.steamID);
          const ready = code.length === 4 && !taken && !busy;

          return (
            <button
              key={player.steamID}
              type="button"
              disabled={taken || busy}
              onClick={() => claim(player)}
              className={
                'flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition ' +
                (taken
                  ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                  : ready
                  ? 'border-lime-300 bg-white hover:border-lime-400 hover:shadow'
                  : 'border-gray-200 bg-white hover:border-gray-300')
              }
            >
              <img
                src={player.imgUrl}
                alt={player.nickName}
                style={{ width: 44, height: 44, borderRadius: '50%' }}
              />
              <span className="min-w-0">
                <span className="block truncate font-medium">{player.nickName}</span>
                <span className="block truncate text-sm text-gray-500">
                  {taken ? 'schon vergeben' : player.name}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {code.length !== 4 && (
        <p className="pt-6 text-sm text-gray-500">Erst den Code eingeben, dann waehlen.</p>
      )}
    </div>
  );
};

export default JoinView;
